import { useState, useEffect, useRef } from 'react';
import { useUserSelfQuery } from '@/features/users/queries';

// ==============================|| LOCAL STORAGE HOOKS ||============================== //

export default function useLocalStorage<ValueType>(key: string, defaultValue: ValueType): [ValueType, (newValue: ValueType) => void] {
  const { data: currentUser } = useUserSelfQuery();
  const prevUserIdRef = useRef<number | null>(null);
  
  // Kullanıcıya özel key oluştur
  const userSpecificKey = currentUser?.id ? `${key}_user_${currentUser.id}` : key;
  
  const [value, setValue] = useState<ValueType>(() => {
    // SSR uyumluluğu için localStorage kontrolü
    if (typeof window === 'undefined') {
      return defaultValue;
    }
    
    const storedValue = localStorage.getItem(userSpecificKey);
    return storedValue === null ? defaultValue : (JSON.parse(storedValue) as ValueType);
  });

  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === userSpecificKey) {
        setValue(e.newValue ? (JSON.parse(e.newValue) as ValueType) : defaultValue);
      }
    };
    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [userSpecificKey, defaultValue]);

  // Kullanıcı değiştiğinde value'yu güncelle (sadece kullanıcı ID'si değiştiğinde)
  useEffect(() => {
    // SSR uyumluluğu için window kontrolü
    if (typeof window === 'undefined') {
      return;
    }
    
    const currentUserId = currentUser?.id || null;
    
    // Kullanıcı değişti mi kontrol et
    if (prevUserIdRef.current !== currentUserId) {
      prevUserIdRef.current = currentUserId;
      
      const storedValue = localStorage.getItem(userSpecificKey);
      const newValue = storedValue === null ? defaultValue : (JSON.parse(storedValue) as ValueType);
      
      // Sadece değer gerçekten farklıysa güncelle
      setValue(prevValue => {
        if (JSON.stringify(prevValue) !== JSON.stringify(newValue)) {
          return newValue;
        }
        return prevValue;
      });
    }
  }, [currentUser?.id, userSpecificKey, defaultValue]);

const setValueInLocalStorage = (newValue: ValueType) => {
  setValue((currentValue: ValueType) => {
    const result = typeof newValue === 'function' ? newValue(currentValue) : newValue;
    
    // SSR uyumluluğu için localStorage kontrolü
    if (typeof window !== 'undefined') {
      localStorage.setItem(userSpecificKey, JSON.stringify(result));
    }
    
    return result;
  });
};

  return [value, setValueInLocalStorage];
}
